/* eslint-disable */

// Extend Window interface for webkitAudioContext support
interface Window {
  webkitAudioContext?: typeof AudioContext;
}

// get correct audio context
let audioContext: AudioContext;
if (typeof window !== "undefined") {
  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
  if (AudioCtx) {
    audioContext = new AudioCtx();
  } else {
    audioContext = {
      createOscillator: () => {},
    } as AudioContext;
  }
} else {
  throw new Error("AudioContext can only be used in a browser environment");
}

// options
export const Options = {
  lowVolume: 0x1,
  mediumVolume: 0x2,
  highVolume: 0x4,

  lowPitch: 0x8,
  mediumPitch: 0x10,
  highPitch: 0x20,

  shortDuration: 0x40,
  mediumDuration: 0x80,
  longDuration: 0x100,

  squareWave: 0x200,
  sineWave: 0x400,
  triangleWave: 0x800,

  wait: 0x1000,
};

const VolumeValues = [
  { option: Options.lowVolume, value: 0.5 },
  { option: Options.mediumVolume, value: 10 },
  { option: Options.highVolume, value: 20 },
];

const PitchValues = [
  { option: Options.lowPitch, value: 110 },
  { option: Options.mediumPitch, value: 220 },
  { option: Options.highPitch, value: 880 },
];

const DurationValues = [
  { option: Options.shortDuration, value: 50 },
  { option: Options.mediumDuration, value: 500 },
  { option: Options.longDuration, value: 1000 },
];

const WaveValues = [
  { option: Options.squareWave, value: "square" },
  { option: Options.sineWave, value: "sine" },
  { option: Options.triangleWave, value: "triangle" },
];

const WaitValues = [{ option: Options.wait, value: true }];

const getProp = <T>(
  props: number,
  optionValues: { option: number; value: string | number | boolean }[],
  defaultValue: T
): T => {
  for (let i = 0; i < optionValues.length; i++) {
    const opt = optionValues[i];
    if (!opt) continue;

    const { option, value } = opt;
    if ((props & option) === option) {
      return value as T;
    }
  }
  return defaultValue;
};

const beep = (
  vol: number,
  freq: number,
  duration: number,
  wave: "square" | "sine" | "triangle"
) => {
  if (process.env.APP_ENV !== "development") return;

  const oscillator = audioContext.createOscillator();
  oscillator.frequency.value = freq;
  oscillator.type = wave;
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration * 0.001);
  const gain = audioContext.createGain();
  gain.gain.value = vol * 0.01;
  gain.connect(audioContext.destination);
  oscillator.connect(gain);
};

const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Make a beep sound
 * @param props bit field of options
 *  - volume: Option.lowVolume, Option.mediumVolume, Option.highVolume
 *  - pitch: Option.lowPitch, Option.mediumPitch, Option.highPitch
 *  - duration: Option.shortDuration, Option.mediumDuration, Option.longDuration
 * - wave: Option.squareWave, Option.sineWave, Option.triangleWave
 * - wait: Option.wait
 *
 * @example
 * ```ts
 * // Beep with default settings
 * Beep();
 *
 * // Beep with custom settings
 * await Beep(Options.highVolume | Options.highPitch | Options.longDuration | Options.sineWave | Options.wait);
 * ```
 */
export const Beep = async (props = 0) => {
  const vol = getProp(props, VolumeValues, 1);
  const pitch = getProp(props, PitchValues, 440);
  const duration = getProp(props, DurationValues, 100);
  const wave = getProp(props, WaveValues, "square");
  const wait = getProp(props, WaitValues, "false");

  beep(vol, pitch, duration, wave);
  if (wait) await sleep(duration);
};

export default Beep;
